import { useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { useStringsContext } from '../../../../contexts/strings';
import { PollExportCSV, PollResultsQuery, PollVoter, PollVotersQuery } from '../types';
import { useUserAuth } from '../../../../contexts/auth';
import adminApi from '../../../../api/admin-api';

const { REACT_APP_GRAPHQL_APIURL } = process.env;

const queryResults = `query Results($pollId: String!) {
  poll(id: $pollId) {
    result {
      pollId
      id
      count
    }
  }
}`;

const queryResultsWithPollOptions = `query Results($pollId: String!) {
  poll(id: $pollId) {
    result {
      pollId
      id
      count
    },
    pollOptions {
      text
      id
    }
  }
}`;

const queryVoters = `query countedVotes($pollId: String!, $onlyVotesOnWinner: Boolean!) {
  countedVotes(id: $pollId, onlyVotesOnWinner: $onlyVotesOnWinner) {
    personId
    email
    name
    votedOnWinner
  }
}`;

const TIMEOUT_MS = 30 * 1000;
const FIVE_MINUTES_MS = 5 * 60 * 1000;

async function fetchWithTimeout(
  url: string,
  options: RequestInit,
  timeoutMs: number = TIMEOUT_MS
): Promise<Response> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal,
    });
    return response;
  } finally {
    clearTimeout(timeoutId);
  }
}

/**
 * Fetch poll results - exported for use with useQueries
 */
export async function fetchPollResults(
  pollId: string,
  includePollOptions = false
): Promise<PollResultsQuery | null> {
  const response = await fetchWithTimeout(REACT_APP_GRAPHQL_APIURL as string, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      query: includePollOptions ? queryResultsWithPollOptions : queryResults,
      variables: { pollId },
    }),
  });

  if (!response.ok) {
    throw new Error(`Get results failed! (${response.status})`);
  }

  const data = await response.json();
  return data?.data?.poll ?? null;
}

export function usePollResults(pollId: string | null, options?: { includePollOptions?: boolean }) {
  const { enqueueSnackbar } = useSnackbar();
  const { strings } = useStringsContext();
  const includePollOptions = options?.includePollOptions ?? false;

  return useQuery({
    queryKey: ['poll', 'results', pollId, includePollOptions],
    queryFn: () => fetchPollResults(pollId!, includePollOptions),
    enabled: !!pollId,
    staleTime: FIVE_MINUTES_MS,
    gcTime: FIVE_MINUTES_MS,
    retry: false,
    meta: {
      onError: () => {
        enqueueSnackbar(strings.messages.loadingError, { variant: 'error' });
      },
    },
  });
}

export function usePollVoters(
  pollId: string | null,
  options?: { onlyVotesOnWinner?: boolean; enabled?: boolean }
) {
  const { enqueueSnackbar } = useSnackbar();
  const { strings } = useStringsContext();
  const onlyVotesOnWinner = options?.onlyVotesOnWinner ?? false;

  return useQuery({
    queryKey: ['poll', 'voters', pollId, onlyVotesOnWinner],
    queryFn: async (): Promise<PollVoter[]> => {
      const response = await fetchWithTimeout(REACT_APP_GRAPHQL_APIURL as string, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          query: queryVoters,
          variables: { pollId, onlyVotesOnWinner },
        }),
      });

      if (!response.ok) {
        throw new Error(`Get voters failed! (${response.status})`);
      }

      const data = await response.json();
      return (data?.data as PollVotersQuery)?.countedVotes ?? [];
    },
    enabled: !!pollId && (options?.enabled ?? true),
    staleTime: FIVE_MINUTES_MS,
    gcTime: FIVE_MINUTES_MS,
    retry: false,
    meta: {
      onError: () => {
        enqueueSnackbar(strings.messages.loadingError, { variant: 'error' });
      },
    },
  });
}

/**
 * Hook for poll-related actions that require authentication or can't use react-query
 */
export const usePoll = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { strings } = useStringsContext();
  const { currentUser } = useUserAuth();

  const getVotersCSV = useCallback(
    async (pollId: string, isQuiz: boolean) => {
      let csvUrl: string | null = null;

      const auth = await currentUser?.getIdToken();
      if (!auth) {
        return null;
      }

      try {
        // const response = await fetch(
        //   `${REACT_APP_USER_ENGAGEMENT_URL}/export/${isQuiz ? 'quiz' : 'poll'}/${pollId}`,
        //   {
        //     headers: {
        //       'Content-Type': 'application/json',
        //       Authorization: `Bearer ${auth}`,
        //     },
        //   }
        // );

        const response = await adminApi.get({
          path: `/polls/export/${isQuiz ? 'quiz' : 'poll'}/${pollId}`,
          auth,
        });

        if (!response.ok) {
          throw new Error(`Get csv export failed! (${response.status})`);
        }

        const data = (await response.json()) as PollExportCSV;

        if (data.error || !data.url) {
          throw new Error(`Get csv export failed! (${data.error})`);
        }

        csvUrl = data.url;
      } catch (error: any) {
        console.error(error);
        enqueueSnackbar(strings.messages.exportError, {
          variant: 'error',
        });
        throw error;
      }

      return csvUrl;
    },
    [currentUser, enqueueSnackbar, strings]
  );

  return {
    getVotersCSV,
  };
};
